import React, { useMemo } from "react";
import { AlertTriangle, Briefcase, MapPin, Clock, User, UserMinus } from "lucide-react";
import { fmtDate } from "@/lib/format";

const timeLabel = (s, e) => (s ? `${s}${e ? `–${e}` : ""}` : "tutto il giorno");

function ConflictRow({ c }) {
  const isTurno = c.kind === "turno";
  return (
    <li className="p-2 rounded-md bg-white border border-amber-200" data-testid="conflict-row">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${isTurno ? "bg-emerald-100 text-emerald-800" : "bg-indigo-100 text-indigo-800"}`}>
          {isTurno ? "Mansione interna" : `Impegno esterno${c.subkind ? ` · ${c.subkind}` : ""}`}
        </span>
        <span className="font-semibold text-stone-800">{c.title || "—"}</span>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-stone-600">
        <span>{fmtDate(c.date)}</span>
        <span className="flex items-center gap-1"><Clock size={11}/> già occupato: <b className="text-stone-800">{timeLabel(c.start_time, c.end_time)}</b></span>
        <span>nuovo: <b className="text-stone-800">{timeLabel(c.new_start, c.new_end)}</b></span>
        {c.location && <span className="flex items-center gap-1"><MapPin size={11}/>{c.location}</span>}
      </div>
    </li>
  );
}

export function ConflictPanel({ warn, force, onForceChange, testIdPrefix, onRemoveStudent }) {
  const groups = useMemo(() => {
    const m = {};
    (warn?.conflicts || []).forEach((c) => { (m[c.student_id] ||= { id: c.student_id, name: c.student_name, items: [] }).items.push(c); });
    return Object.values(m).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [warn]);
  if (!warn) return null;
  const all = warn.conflicts || [];
  const total = warn.conflict_count || all.length;
  const nTurni = all.filter((c) => c.kind === "turno").length;
  const nImp = all.length - nTurni;

  return (
    <div className="p-3 rounded-lg bg-amber-50 border border-amber-300 text-xs space-y-2" data-testid={`${testIdPrefix}-conflicts`}>
      <div className="flex items-start gap-1.5 font-semibold text-amber-900 text-sm">
        <AlertTriangle size={16} className="shrink-0 mt-0.5"/> Sovrapposizione rilevata
      </div>
      <div className="text-amber-900" data-testid={`${testIdPrefix}-conflicts-summary`}>
        {groups.length} studente/i · {total} conflitto/i
        {nTurni > 0 && <> · <b>{nTurni}</b> con mansioni interne</>}
        {nImp > 0 && <> · <b>{nImp}</b> con impegni esterni</>}
        {total > all.length && <> (mostrati i primi {all.length})</>}
      </div>
      <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
        {groups.map((g) => (
          <div key={g.id || g.name}>
            <div className="flex items-center gap-1 font-medium text-stone-800 mb-1"><User size={12}/>{g.name || "Studente"} <span className="text-stone-500 font-normal">({g.items.length})</span>
              {onRemoveStudent && g.id && (
                <button type="button" onClick={() => onRemoveStudent(g.id)} data-testid={`${testIdPrefix}-remove-conflict-${g.id}`}
                  className="ml-auto inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 transition-colors">
                  <UserMinus size={11}/> Togli dall'impegno
                </button>
              )}
            </div>
            <ul className="space-y-1">{g.items.map((c, i) => <ConflictRow key={i} c={c}/>)}</ul>
          </div>
        ))}
      </div>
      {onRemoveStudent && <div className="text-[11px] text-amber-900/80">Puoi togliere lo studente e sceglierne un altro dall'elenco: poi premi di nuovo «Crea impegno» per verificare.</div>}
      <label className="flex items-center gap-2 cursor-pointer pt-1 border-t border-amber-200">
        <input type="checkbox" checked={force} onChange={(e) => onForceChange(e.target.checked)} data-testid={`${testIdPrefix}-force`}/>
        <span className="text-amber-900 font-medium flex items-center gap-1"><Briefcase size={12}/> Ho verificato, forza comunque la sovrapposizione</span>
      </label>
    </div>
  );
}
